import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  MapPin,
  Phone,
  CheckCircle2,
  Sparkles,
  BookOpen,
  Wrench,
  Star,
  TrendingUp,
  Award,
  Users,
  Quote,
} from "lucide-react";

// Per-city testimonials + highlight metrics live in cityTestimonials.ts so
// each Colorado city page renders unique social proof (names, trades,
// neighborhoods, and metrics) rather than the same Springs block sitewide.

// Global service hubs we cross-link from every city page (anchor text is
// identical across cities so internal linking signals stay consistent).
const SERVICE_LINKS = [
  { to: "/colorado-springs-seo" as const, anchor: "Local SEO services in Colorado" },
  { to: "/colorado-springs-web-design" as const, anchor: "Colorado web design services" },
  { to: "/colorado-springs-google-maps" as const, anchor: "Google Maps optimization in Colorado" },
  { to: "/colorado-springs-ppc" as const, anchor: "Google Ads management in Colorado" },
];

// Curated Learn (Blog) articles every city page links to, with consistent anchor text.
const HUB_ARTICLES = [
  {
    to: "/learn/local-seo-guide" as const,
    anchor: "The plain-English guide to local SEO for Colorado small businesses",
  },
  {
    to: "/learn/google-business-profile" as const,
    anchor: "How to rank in the Google Maps 3-pack",
  },
  {
    to: "/learn/website-conversion" as const,
    anchor: "Why your website gets traffic but no calls",
  },
  {
    to: "/learn/service-area-pages" as const,
    anchor: "Service area & city landing pages: how to do them right",
  },
  {
    to: "/learn/get-more-reviews" as const,
    anchor: "How to get 5-star Google reviews from Colorado customers",
  },
  {
    to: "/learn/seo-vs-ppc" as const,
    anchor: "SEO vs Google Ads: which should a Colorado business invest in first?",
  },
];

import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { PageCTA } from "@/components/PageCTA";
import { IndustriesForCityLinks } from "@/components/CrossLinkMesh";
import { CITY_PAGE_SLUGS, getCityPage, type CityPage } from "@/lib/citiesData";
import { getCityProof } from "@/lib/cityTestimonials";
import { StickyLocalCTA } from "@/components/StickyLocalCTA";
import { StaticReviews } from "@/components/StaticReviews";
import { getCombosForCity, getCityAnchorVariant } from "@/lib/cityServiceMatrix";
import heroColorado from "@/assets/hero-colorado.jpg";
import { cityHubHeroAlt } from "@/lib/imageAlt";
import { ogImageFor, ogMetaTags, twitterTextTags, type OgCityKey } from "@/lib/ogImages";

const SITE_URL = "https://coloradowebimpressions.com";
const PHONE = "+1-719-419-3935";
const PHONE_DISPLAY = "(719) 419-3935";

function buildLocalBusinessJsonLd(city: CityPage, ogImageAbsolute: string) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    // City-scoped sub-node with its own geo/areaServed, linked to the canonical
    // Organization so Google's Knowledge Graph stitches them together.
    "@id": `${SITE_URL}/service-areas/${city.slug}#localbusiness`,
    parentOrganization: { "@id": `${SITE_URL}/#org` },
    name: `Colorado Web Impressions — ${city.name}`,
    description: city.metaDescription,
    url: `${SITE_URL}/service-areas/${city.slug}`,
    telephone: PHONE,
    email: "info@coloradowebimpressions.com",
    priceRange: "$$",
    image: ogImageAbsolute,
    address: {
      "@type": "PostalAddress",
      streetAddress: "3210 E Woodmen Rd STE 110",
      addressLocality: "Colorado Springs",
      addressRegion: "CO",
      postalCode: "80920",
      addressCountry: "US",
    },
    areaServed: {
      "@type": "City",
      name: city.name,
      containedInPlace: {
        "@type": "State",
        name: "Colorado",
      },
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: city.geo.lat,
      longitude: city.geo.lng,
    },
    sameAs: [
      "https://www.facebook.com/coloradowebimpressions/",
      "https://www.instagram.com/coloradowebimpressions/",
      "https://www.linkedin.com/company/colorado-web-impressions/",
    ],
  };
}

function buildServiceJsonLd(city: CityPage) {
  // Each service offered, scoped to this city via areaServed.
  return city.servicesOffered.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.name,
    description: s.description,
    serviceType: s.name,
    provider: {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/service-areas/${city.slug}#localbusiness`,
      name: "Colorado Web Impressions",
    },
    areaServed: {
      "@type": "City",
      name: city.name,
      containedInPlace: { "@type": "State", name: "Colorado" },
    },
    url: `${SITE_URL}/service-areas/${city.slug}`,
  }));
}

function buildFaqJsonLd(city: CityPage) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: city.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

function buildBreadcrumbJsonLd(city: CityPage) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${SITE_URL}/service-areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: city.name,
        item: `${SITE_URL}/service-areas/${city.slug}`,
      },
    ],
  };
}

export const Route = createFileRoute("/service-areas/$citySlug")({
  loader: ({ params }) => {
    const city = getCityPage(params.citySlug);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "City | Colorado Web Impressions" }] };
    const { city } = loaderData;
    const canonical = `${SITE_URL}/service-areas/${city.slug}`;
    // Per-city OG image (falls back to default for any city not yet mapped).
    const ogImage = `${SITE_URL}${ogImageFor({ city: city.slug as OgCityKey })}`;
    // Twitter-specific copy: tighter, benefit-led for fast feed scanning.
    const twitterTitle = `${city.name} Marketing — Web Design, SEO & Google Maps`;
    const twitterDescription = `Local marketing for ${city.name}, CO businesses: lead-gen websites, SEO that ranks, and Google Maps that calls. Owner-led, no contracts.`;
    return {
      meta: [
        { title: city.metaTitle },
        { name: "description", content: city.metaDescription },
        { property: "og:title", content: city.metaTitle },
        { property: "og:description", content: city.metaDescription },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "website" },
        ...ogMetaTags(ogImage),
        ...twitterTextTags({ title: twitterTitle, description: twitterDescription }),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildLocalBusinessJsonLd(city, ogImage)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildServiceJsonLd(city)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqJsonLd(city)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildBreadcrumbJsonLd(city)),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <SiteLayout>
      <PageHero
        eyebrow="Not found"
        title={<>City page not found</>}
        description="We couldn't find that city. Browse all Colorado service areas below."
      >
        <Link
          to="/service-areas"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
        >
          See all service areas
          <ArrowRight className="h-5 w-5" />
        </Link>
      </PageHero>
    </SiteLayout>
  ),
  component: CityPageComponent,
});

function CityPageComponent() {
  const { city } = Route.useLoaderData() as { city: CityPage };
  const heroParts = city.heroTitle.split(city.heroGoldWord);
  // City-specific service spokes — these are the dedicated landing pages
  // (/{city}-seo, /{city}-web-design, /{city}-local-seo). Held out of the
  // sitemap for non-priority cities, so this internal-link block is the
  // primary discovery path for crawlers.
  const cityServiceCombos = getCombosForCity(city.slug);
  const proof = getCityProof(city.slug);

  return (
    <SiteLayout>
      <PageHero
        eyebrow={city.heroEyebrow}
        title={
          <>
            {heroParts[0]}
            <span className="text-gradient-gold">{city.heroGoldWord}</span>
            {heroParts[1] ?? ""}
          </>
        }
        description={city.heroDescription}
        image={{
          src: heroColorado,
          alt: cityHubHeroAlt(city.name, city.state),
        }}
      >
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free Proposal
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
           aria-label="Call (719) 419-3935">
            <Phone className="h-5 w-5" />
            {PHONE_DISPLAY}
          </a>
        </div>
      </PageHero>

      {/* Intro */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              {city.region} · {city.state}
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Local marketing for {city.name} businesses
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{city.intro}</p>
          </div>

          {/* Neighborhoods */}
          <div className="mt-10 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold text-foreground">
              <MapPin className="h-4 w-4 text-brand-gold-deep" />
              Neighborhoods we focus on in {city.name}
            </div>
            <div className="flex flex-wrap gap-2">
              {city.neighborhoods.map((n) => (
                <span
                  key={n}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1.5 text-xs font-semibold text-foreground"
                >
                  <MapPin className="h-3 w-3 text-brand-gold-deep" />
                  {n}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why we win here */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Why we get results in {city.name}
            </h2>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {city.whyHere.map((w) => (
              <div
                key={w.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-gold text-brand-black">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why this market — long-tail topical SEO copy.
          Renders only when the city has whyThisMarket paragraphs filled in.
          Each paragraph targets that city's specific long-tail keywords
          (trades, neighborhoods, seasonality) so the city page is not a
          templated copy of the others. */}
      {city.whyThisMarket && city.whyThisMarket.length > 0 && (
        <section className="bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <div className="text-center">
              <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                Built for the {city.name} market
              </span>
              <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                Why {city.name} businesses search differently
              </h2>
              <p className="mt-3 text-muted-foreground">
                The keywords, seasons, and buyer behavior that make {city.name}{" "}
                its own market — not a templated copy of every other Front Range city.
              </p>
            </div>
            <div className="mx-auto mt-12 max-w-3xl space-y-10">
              {city.whyThisMarket.map((p) => (
                <article key={p.heading}>
                  <h3 className="text-xl font-bold text-foreground sm:text-2xl">
                    {p.heading}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Per-city topical SEO block — surfaces topicalCopy + topicalKeywords on
          the city hub itself (also rendered on each /seo/{city}, /web-design/{city},
          /google-maps/{city} spoke). Keeps the hub page from feeling templated. */}
      {(city.topicalCopy || (city.topicalKeywords && city.topicalKeywords.length > 0)) && (
        <section className="bg-section-alt py-20 lg:py-28">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            {city.topicalCopy && (
              <>
                <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
                  {city.name} topical optimization
                </span>
                <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                  {city.topicalCopy.heading}
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  {city.topicalCopy.body}
                </p>
              </>
            )}
            {city.topicalKeywords && city.topicalKeywords.length > 0 && (
              <div className={city.topicalCopy ? "mt-10" : ""}>
                <p className="text-sm font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
                  What {city.name} customers actually search
                </p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {city.topicalKeywords.map((kw) => (
                    <li
                      key={kw}
                      className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground/80 shadow-sm"
                    >
                      {kw}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Per-city proof — testimonials, highlights, focused CTA */}
      <section className="bg-gradient-dark py-20 text-brand-cream lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
              {proof.eyebrow}
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Real results for real {city.name} businesses
            </h2>
            <p className="mt-3 text-brand-cream/80">
              Every quote, neighborhood, and metric below is from work in the {city.name} market —
              not a stock testimonial recycled from another city. Here's what local proof looks like.
            </p>
          </div>

          {/* Highlights */}
          <div className="mx-auto mt-10 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {proof.highlights.map((h) => (
              <div
                key={h.metric}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur transition-spring hover:-translate-y-1 hover:border-brand-gold/50"
              >
                <div className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-gradient-gold text-brand-black">
                  <h.icon className="h-5 w-5" />
                </div>
                <div className="mt-3 text-2xl font-extrabold text-white">{h.metric}</div>
                <div className="mt-1 text-xs leading-relaxed text-brand-cream/85">{h.label}</div>
              </div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {proof.testimonials.map((t) => (
              <figure
                key={t.name + t.business}
                className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur"
              >
                <Quote className="h-7 w-7 text-brand-gold" />
                <blockquote className="mt-3 flex-1 text-sm italic leading-relaxed text-brand-cream/90">
                  "{t.quote}"
                </blockquote>
                <div className="mt-4 flex items-center gap-1 text-brand-gold">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <figcaption className="mt-3 border-t border-white/10 pt-3 text-xs">
                  <div className="font-bold text-white">{t.name}</div>
                  <div className="text-brand-cream/85">
                    {t.business} · {t.location}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Conversion-focused CTA */}
          <div className="relative mt-12 overflow-hidden rounded-3xl border border-white/15 bg-black/30 p-8 text-center shadow-card backdrop-blur sm:p-12">
            <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand-gold/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-brand-gold/10 blur-3xl" />
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-gold" />

            <div className="relative">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold-deep/40 bg-brand-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand-gold">
                <Sparkles className="h-3.5 w-3.5" />
                Free 30-minute strategy call
              </span>
              <h3 className="mt-4 text-2xl font-extrabold text-white sm:text-3xl">
                Want results like this for your {city.name} business?
              </h3>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-white/90 sm:text-base">
                We'll review your current website, Google Business Profile, and top competitors —
                and give you a clear, no-pressure plan to win more {city.name} customers. No
                contracts, no hard sell.
              </p>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-white/90">
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-gold" />
                  No long-term contracts
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-gold" />
                  Real Colorado team
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand-gold" />
                  Transparent pricing
                </span>
              </div>

              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 rounded-xl bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
                >
                  Book my free {city.name} strategy call
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href={`tel:${PHONE}`}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
                 aria-label="Call (719) 419-3935">
                  <Phone className="h-5 w-5" />
                  Call {PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Services we offer in {city.name}
            </h2>
            <p className="mt-3 text-muted-foreground">
              Everything you need to rank, get found, and convert local {city.name} customers.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {city.servicesOffered.map((s) => (
              <div
                key={s.name}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-brand-gold-deep" />
                  <h3 className="text-base font-bold text-foreground">{s.name}</h3>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-3xl border border-border bg-gradient-dark p-8 text-center text-brand-cream sm:p-10">
            <h3 className="text-2xl font-extrabold text-white sm:text-3xl">
              Ready to grow your {city.name} business?
            </h3>
            <p className="mx-auto mt-3 max-w-2xl text-brand-cream/80">
              We'll audit your current rankings and show you a clear, no-pressure path to more
              calls, leads, and booked work in {city.name}.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                Get a Free Proposal
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
              >
                See transparent pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Resources & cross-links */}
      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
              Keep going
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Resources for {city.name} businesses
            </h2>
            <p className="mt-3 text-muted-foreground">
              Dig deeper into how we'd grow your {city.name} business with our core services and
              free guides.
            </p>
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            {/* Services column */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-brand-gold-deep" />
                <h3 className="text-lg font-bold text-foreground">
                  Services we offer in {city.name}
                </h3>
              </div>

              {/* City-specific service spokes — these are the dedicated
                  /{city}-{service} landing pages with unique long-tail copy
                  for this market. Rendered as <a> (not Link) because each
                  spoke is a separate file route with a different param shape;
                  a plain anchor is crawled identically and avoids juggling
                  three different typed Link calls per combo. */}
              {cityServiceCombos.length > 0 && (
                <>
                  <p className="mt-4 text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                    Dedicated {city.name} service pages
                  </p>
                  <ul className="mt-3 space-y-3">
                    {cityServiceCombos.map((combo, idx) => {
                      // Rotate anchor pattern by city-slug length + service idx
                      // so different cities feature different anchor variants
                      // for the same service across the site (avoids identical
                      // sitewide anchors) while staying stable per page.
                      const variantIdx = city.slug.length + idx;
                      const anchorText = getCityAnchorVariant(
                        combo.service.key,
                        city.name,
                        variantIdx,
                      );
                      return (
                        <li key={combo.path}>
                          <a
                            href={combo.path}
                            className="group inline-flex items-start gap-2 text-sm font-semibold text-foreground transition-smooth hover:text-brand-gold-deep"
                          >
                            <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-0.5" />
                            <span className="underline-offset-4 group-hover:underline">
                              {anchorText}
                            </span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              <p className="mt-6 text-xs font-bold uppercase tracking-wider text-brand-gold-deep">
                All Colorado services
              </p>
              <ul className="mt-3 space-y-3">
                {SERVICE_LINKS.map((s) => (
                  <li key={s.to}>
                    <Link
                      to={s.to}
                      className="group inline-flex items-start gap-2 text-sm font-semibold text-foreground transition-smooth hover:text-brand-gold-deep"
                    >
                      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-0.5" />
                      <span className="underline-offset-4 group-hover:underline">{s.anchor}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Articles column */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-brand-gold-deep" />
                <h3 className="text-lg font-bold text-foreground">
                  Free guides from our Learn (Blog)
                </h3>
              </div>
              <ul className="mt-4 space-y-3">
                {HUB_ARTICLES.map((a) => (
                  <li key={a.to}>
                    <Link
                      to={a.to}
                      className="group inline-flex items-start gap-2 text-sm font-semibold text-foreground transition-smooth hover:text-brand-gold-deep"
                    >
                      <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-0.5" />
                      <span className="underline-offset-4 group-hover:underline">{a.anchor}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/learn"
                className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-gold-deep hover:underline"
              >
                Browse the full Learn (Blog) →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-section-alt py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              {city.name} marketing FAQs
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {city.faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <h3 className="text-base font-bold text-foreground">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-muted-foreground">
            Don't see your city?{" "}
            <Link
              to="/service-areas"
              className="font-semibold text-brand-gold-deep hover:underline"
            >
              Browse all Colorado service areas →
            </Link>
          </p>
        </div>
      </section>

      <IndustriesForCityLinks cityName={city.name} />

      <StaticReviews
        eyebrow="Google reviews"
        heading={`What ${city.name === "Colorado Springs" ? "Springs" : "Colorado"} clients are saying`}
        description="Verified reviews from our Google Business Profile. See the full live feed on our reviews page."
      />

      <PageCTA
        headline={`Want results like this for your ${city.name} business?`}
        description={`We'll review your current website, Google Business Profile, and top ${city.name} competitors — then give you a clear, no-pressure plan to win more local customers.`}
        seeOurWork={{
          to: "/reviews",
          label: `See verified reviews from ${city.name === "Colorado Springs" ? "Springs" : "Colorado"} clients`,
        }}
      />
      <StickyLocalCTA context={city.name} />
    </SiteLayout>
  );
}

export { CITY_PAGE_SLUGS };
